// Lab Partner Portal — Dashboard (Overview)
//
// Replaces the medical-portal Overview that the lab portal was reusing.
// Sections:
//   1. Greeting block (kicker pill, big "Welcome, Dr. <Name>." headline,
//      org · tier · territory lede)
//   2. 4-stat strip (Reqs MTD, In Progress, Margin MTD, Tests Available)
//   3. Quick Actions — 8 tiles in a 4-col grid
//   4. Recent Requisitions table — real visits from the sales API
//      (one row per visit, clickable to open ReportDetailModal)
//   5. Branch TAT Performance — 3-col mini grid with Avg TAT + on-time %
//
// The L2L pricing banner was removed — partner-tier pricing data isn't
// wired in yet, so the static "35–45%" claim was actively misleading.
// Re-add once the per-partner rate lookup is available.
import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import SearchIcon from '@mui/icons-material/SearchOutlined';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import ApiIcon from '@mui/icons-material/ApiOutlined';
import PaletteIcon from '@mui/icons-material/PaletteOutlined';
import AnalyticsIcon from '@mui/icons-material/InsightsOutlined';
import AddCircleIcon from '@mui/icons-material/AddCircleOutline';

import { MP, MONO, DISPLAY, StatCard, groupByVisit, prettyDate } from '../medical/shared';
import ReportDetailModal from '../medical/ReportDetailModal';
import { useAuth } from '../../../context/AuthContext';
import { useUI } from '../../../context/UIContext';
import {
  useGetSalesByCustomerQuery,
  useGetSalesByCustomerAndDoctorQuery,
} from '../../../store/salesApi';
import { useListRcaInvestigationsQuery } from '../../../store/queriesApi';
import { useGetTestsListQuery } from '../../../store/edosApi';
import {
  useGetTatReportQuery,
  useGetTatReportByCustomerAndDoctorQuery,
} from '../../../store/tatReportApi';
import Skeleton from '@mui/material/Skeleton';

// ─── Last-7-days-ending-yesterday helper ───────────────────────────
// Today's data is still settling (samples drawn this morning won't be
// approved until evening), so the dashboard TAT card mirrors the
// Analytics page and ends the window at YESTERDAY. Inclusive 7 days
// stays under the IT Dose 7-day cap.
const dashYmd = (d) => d.toISOString().split('T')[0];
const last7EndingYesterday = () => {
  const end = new Date();
  end.setDate(end.getDate() - 1);
  const start = new Date(end);
  start.setDate(end.getDate() - 6);
  return { from: dashYmd(start), to: dashYmd(end) };
};

// Parse IT Dose "HH:MM:SS" timing strings into minutes (returns null
// for unparseable entries so they don't poison the average).
const parseHrsMinSecLO = (raw) => {
  const m = String(raw || '').trim().match(/^(\d+):(\d{2}):(\d{2})$/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]) + Number(m[3]) / 60;
};

const formatMinutesLO = (mins) => {
  if (mins == null || !Number.isFinite(mins)) return '—';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

// Mirrors LabDashboard.js — the badge there uses the same status enum
// so the dashboard and the sidebar always agree on what's "open".
const ACTIVE_QUERY_STATES = new Set(['received', 'investigating', 'engaged']);
const isActiveRca = (rec) =>
  ACTIVE_QUERY_STATES.has(String(rec?.view_status_label || '').toLowerCase());
const isResolvedRca = (rec) => {
  const v = String(rec?.view_status_label || '').toLowerCase();
  return v === 'resolved' || v === 'closed';
};

// Visits whose status indicates the lab has signed off the report.
// Everything else counts as "in progress / pending" for the operational
// strip. Status strings are free-form from the sales feed — keep the
// match generous so we don't undercount false negatives.
const REPORT_FINAL_STATES = ['approved', 'dispatched', 'reported', 'released'];
const isVisitFinal = (visit) => {
  const v = String(visit?.status || '').toLowerCase();
  return REPORT_FINAL_STATES.some((s) => v.includes(s));
};

// "Apr", "May", … — used for the "Requisitions · {Month}" label.
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "01-Apr-2026" → { y: 2026, m: 3 (0-indexed) }. Returns null for any
// shape the sales feed doesn't emit — protects the stat math from
// blowing up on a rogue row.
const parseBillDate = (raw) => {
  const m = String(raw || '').match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})/);
  if (!m) return null;
  const mi = MONTH_SHORT.findIndex((s) => s.toLowerCase() === m[2].toLowerCase());
  if (mi < 0) return null;
  return { y: Number(m[3]), m: mi };
};

// ---------- Mock data (mirrors the eDOS partner-dashboard screenshot) ----------
const PARTNER_TIER = 'CCF Founding Partner';
const PARTNER_TERRITORY = 'Hyderabad West (Begumpet · Ameerpet · Punjagutta)';

// Quick action tiles. `nav` is the LabDashboard menu key to switch to;
// `null` means we don't have a target yet — those show a "Coming soon"
// snackbar so the UI matches the screenshot but nothing breaks.
const QUICK_ACTIONS = [
  { id: 'browse',     icon: SearchIcon,       title: 'Browse Catalogue',      sub: '1,200+ tests with your partner pricing visible against MRP.',         nav: 'edos-tests' },
  { id: 'trfs',       icon: DescriptionIcon,  title: 'Download TRFs',         sub: 'Branch-specific Test Requisition Forms in print-ready PDF.',         nav: 'requisition' },
  { id: 'menus',      icon: MenuBookIcon,     title: 'MR Menus',              sub: 'Specialty-specific test menus, your L2L pricing, ready to print.',   nav: null },
  { id: 'history',    icon: ReceiptLongIcon,  title: 'Requisition History',   sub: 'Full sample-tracking log, exportable to CSV for your records.',      nav: 'reports' },
  { id: 'api',        icon: ApiIcon,          title: 'API & HL7',             sub: 'Push orders & pull reports from your HIS/LIS. ABDM-ready.',          nav: null },
  { id: 'marketing',  icon: PaletteIcon,      title: 'Marketing Kit',         sub: 'Co-branded posters, social cards, and patient-facing leaflets.',     nav: null },
  { id: 'outcomes',   icon: AnalyticsIcon,    title: 'Patient Outcomes',      sub: 'De-identified analytics on conditions you order most.',              nav: 'analytics' },
  { id: 'refer',      icon: AddCircleIcon,    title: 'Refer a Test',          sub: 'Need a test outside the catalogue? Submit a request.',               nav: 'new-query' },
];

// Brand tokens — picking up the green/yellow that the eDOS mockup uses
// for the dark hero and the L2L accent. Kept inline here (rather than
// extending shared.js) since these are dashboard-specific.
const GREEN_DEEP = '#10151C';
const TEAL_DEEP  = '#008976';

// ---------- Atoms ----------

// Stat tile — borderless inside a single bordered strip. `accent` tints
// the value text only (top accent bar removed to match the screenshot's
// flat 4-up).
const Stat = ({ value, label, hint, accent }) => (
  <Box sx={{ p: 2.75, display: 'flex', flexDirection: 'column' }}>
    <Typography
      sx={{
        fontFamily: DISPLAY,
        fontSize: 40,
        lineHeight: 1,
        color: accent || MP.dark,
        letterSpacing: '0.5px',
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontFamily: MONO,
        fontSize: 10.5,
        textTransform: 'uppercase',
        letterSpacing: '0.14em',
        color: MP.textMuted,
        mt: 1,
        fontWeight: 700,
      }}
    >
      {label}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: 12, color: MP.textMuted, mt: 0.5 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

// Section header — display title + optional right-side action link.
const SectionHead = ({ title, right }) => (
  <Stack
    direction="row"
    alignItems="baseline"
    justifyContent="space-between"
    sx={{
      borderBottom: `1px solid ${MP.border}`,
      pb: 1.5,
      mt: 4,
      mb: 2.5,
    }}
  >
    <Typography
      sx={{
        fontFamily: DISPLAY,
        fontSize: 22,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: GREEN_DEEP,
      }}
    >
      {title}
    </Typography>
    {right && (
      <Typography
        component="span"
        sx={{
          fontFamily: MONO,
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: MP.tealDark,
          cursor: 'pointer',
          '&:hover': { color: GREEN_DEEP },
        }}
      >
        {right}
      </Typography>
    )}
  </Stack>
);

// Status pill — reused for the requisition table.
const StatusPill = ({ status }) => {
  const map = {
    'reported':    { bg: MP.greenLight, fg: MP.green,   label: 'Reported',    dot: MP.green },
    'in-progress': { bg: MP.yellowLight, fg: '#7A5705', label: 'In Progress', dot: MP.yellow },
    'collected':   { bg: '#E5EEF6',     fg: '#1F5C8B',  label: 'Collected',   dot: '#1F5C8B' },
    'rejected':    { bg: MP.redLight,   fg: MP.red,     label: 'Rejected',    dot: MP.red },
  };
  const s = map[status] || map['reported'];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        bgcolor: s.bg,
        color: s.fg,
        fontFamily: MONO,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        px: 1,
        py: 0.4,
        borderRadius: '2px',
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: s.dot }} />
      {s.label}
    </Box>
  );
};

// ---------- Main component ----------
const LabOverview = ({ onNavigate }) => {
  const { user, isDoctor, doctor } = useAuth();
  const { showToast } = useUI();
  // Display name — flips to the doctor's name when this is a
  // doctor-mode session (the `user` row in that case is the centre
  // admin's, not the doctor's). The centre's friendly name moves to
  // the subtitle so the doctor sees "at <Centre>" right under the
  // greeting.
  const displayName = isDoctor && doctor?.name
    ? `Dr. ${String(doctor.name).replace(/^Dr\.?\s+/i, '')}`
    : (user?.contact_person || user?.name || user?.lab_name || 'Partner');
  // Centre/hospital name. For a doctor session, the centre's own
  // User row (looked up server-side at login by client_id, with
  // user_type != 'doctor') is the source of truth — surfaced as
  // user.centre_name. Falls back to RefDoctor.hospital from the IT
  // Dose sync if the centre row hadn't been onboarded yet, and to
  // the user's own lab_name / company_name for centre sessions. The
  // (!isDoctor ? user?.name : '') guard prevents the doctor's own
  // name ("Dr. X") from leaking into the centre slot — that bug was
  // why the hero used to say "Doctor at Dr. X".
  const orgName =
    (isDoctor && (user?.centre_name || doctor?.hospital)) ||
    user?.lab_name ||
    user?.client_name ||
    (!isDoctor ? user?.name : '') ||
    'Centre';

  // ─── Real data feeds — same Cloud Function the Patient Reports page
  // reads, just with a beefier page so the stats can cover a typical
  // monthly volume in a single request. pageSize=200 is enough for
  // most labs' MTD numbers without paginating.
  const clientId = user?.client_id;
  const itdoseDoctorId = user?.doctor?.itdose_doctor_id;
  // Doctor session sees their own DoctorID slice; centre admins see
  // every visit. Both hooks always mount but only one fires.
  const centreSales = useGetSalesByCustomerQuery(
    { customerId: clientId, page: 1, pageSize: 200 },
    { skip: !clientId || isDoctor },
  );
  const doctorSales = useGetSalesByCustomerAndDoctorQuery(
    { customerId: clientId, doctorId: itdoseDoctorId, page: 1, pageSize: 200 },
    { skip: !clientId || !isDoctor || !itdoseDoctorId },
  );
  const { data: salesData, isLoading: salesLoading } = isDoctor ? doctorSales : centreSales;
  // RCA queries — drives the "Open Queries" + "Resolved This Month" tiles.
  const { data: rcaData } = useListRcaInvestigationsQuery(
    { clientId },
    { skip: !clientId },
  );

  // ─── TAT Performance card — last 7 days ending yesterday ──────────
  // Reuses the same backend endpoint as the Analytics page (scoped by
  // PanelCode and, for doctors, Doctor_ID). We pin the range so the
  // dashboard card is always "last week" — no picker on this surface.
  // useMemo keeps the range object reference stable across renders so
  // RTK Query doesn't see a new args object each tick.
  const tatRange = useMemo(last7EndingYesterday, []);
  // Two hooks always mount (React hook rules) but only one fires —
  // the other is skipped. Centre admins see every PanelCode row;
  // doctors see only their own Doctor_ID slice. Same branching
  // pattern as the patient-reports section above.
  const tatCentreQuery = useGetTatReportQuery(
    { fromDate: tatRange.from, toDate: tatRange.to },
    { skip: !clientId || isDoctor },
  );
  const tatDoctorQuery = useGetTatReportByCustomerAndDoctorQuery(
    { fromDate: tatRange.from, toDate: tatRange.to, doctorId: itdoseDoctorId },
    { skip: !clientId || !isDoctor || !itdoseDoctorId },
  );
  const {
    data: tatData,
    isLoading: tatLoading,
    isFetching: tatFetching,
  } = isDoctor ? tatDoctorQuery : tatCentreQuery;

  const tatStats = useMemo(() => {
    const rows = tatData?.data || [];
    let mins = [];
    let inTat = 0;
    let outTat = 0;
    for (const r of rows) {
      const m = parseHrsMinSecLO(r?.['Booking-Approval(hrs)']);
      if (m != null) mins.push(m);
      const tag = String(r?.INOutTAT || '').trim().toLowerCase();
      if (tag === 'in tat') inTat += 1;
      else if (tag === 'out tat') outTat += 1;
    }
    const total = rows.length;
    const avg = mins.length ? mins.reduce((a, b) => a + b, 0) / mins.length : null;
    const denom = inTat + outTat;
    const onTimePct = denom ? Math.round((inTat / denom) * 100) : null;
    return { total, avg, onTimePct, inTat, outTat };
  }, [tatData]);

  // EDOS catalogue count — we just need pagination.total, so we ask for
  // the smallest page possible (1 row). The response is cached for 5
  // minutes globally; this hits the network at most twice an hour even
  // across page navigations.
  const { data: edosCountData } = useGetTestsListQuery({ page: 1, perPage: 1 });
  const totalTestsAvailable = edosCountData?.pagination?.total ?? null;

  // Group rows into per-visit objects (visitNo + tests array etc).
  const allVisits = useMemo(
    () => groupByVisit(salesData?.data || []),
    [salesData],
  );
  const recentVisits = useMemo(() => allVisits.slice(0, 5), [allVisits]);

  // ─── Stat calculations ───────────────────────────────────────────────
  // All eight tiles below are derived from the two queries above.
  const monthStats = useMemo(() => {
    const now      = new Date();
    const thisY    = now.getFullYear();
    const thisM    = now.getMonth();             // 0-indexed
    const prev     = new Date(thisY, thisM - 1, 1);
    const prevY    = prev.getFullYear();
    const prevM    = prev.getMonth();
    const thisLabel = MONTH_SHORT[thisM];
    const prevLabel = MONTH_SHORT[prevM];

    let thisCount = 0;
    let prevCount = 0;
    let inProgress = 0;
    let pending = 0;
    // Individual tests run THIS month, across every visit. Pairs with
    // the visit-count tile: Requisitions=visits, Tests Processed=line
    // items. Average is derived at the end.
    let testsThisMonth = 0;
    for (const v of allVisits) {
      // In-progress / pending depend on the current visit status only
      // (not on date) — these are right-now operational counters.
      if (!isVisitFinal(v)) {
        inProgress += 1;
        pending += 1;  // same logical bucket; kept as separate fields
                       // so we can split them later if needed
      }
      const d = parseBillDate(v.billDate);
      if (!d) continue;
      if (d.y === thisY && d.m === thisM) {
        thisCount += 1;
        testsThisMonth += Array.isArray(v.tests) ? v.tests.length : 0;
      }
      else if (d.y === prevY && d.m === prevM) prevCount += 1;
    }
    const avgTestsPerVisit = thisCount > 0 ? (testsThisMonth / thisCount) : 0;

    // Month-over-month delta. Show "—" when we have no prior baseline
    // (first month after onboarding) instead of a misleading "Up ∞%".
    let momHint;
    if (prevCount === 0 && thisCount === 0) momHint = `No activity vs ${prevLabel}`;
    else if (prevCount === 0)               momHint = `New this month`;
    else {
      const pct = Math.round(((thisCount - prevCount) / prevCount) * 100);
      const arrow = pct >= 0 ? '↑' : '↓';
      momHint = `${arrow} ${Math.abs(pct)}% vs ${prevLabel}`;
    }

    return {
      thisCount,
      thisLabel,
      inProgress,
      pending,
      momHint,
      testsThisMonth,
      avgTestsPerVisit,
    };
  }, [allVisits]);

  // RCA counts. Resolved-this-month uses `created_at` as the proxy —
  // closest field we have without a dedicated `resolved_at`. Fine for a
  // dashboard summary; precise stats live in the queries page.
  const rcaStats = useMemo(() => {
    const records = rcaData?.records || [];
    const now = new Date();
    const thisY = now.getFullYear();
    const thisM = now.getMonth();

    let open = 0;
    let resolvedThisMonth = 0;
    let resolvedTotal = 0;
    for (const r of records) {
      if (isActiveRca(r)) open += 1;
      if (isResolvedRca(r)) {
        resolvedTotal += 1;
        const d = new Date(r.created_at || r.updated_at || 0);
        if (!Number.isNaN(d.getTime()) && d.getFullYear() === thisY && d.getMonth() === thisM) {
          resolvedThisMonth += 1;
        }
      }
    }
    return { open, resolvedThisMonth, resolvedTotal };
  }, [rcaData]);

  // Row-click modal — identical contract to PatientReports' usage so
  // the visit hand-off + Raise-Query prefill keep working consistently.
  const [reportModal, setReportModal] = useState({ open: false, id: null, visit: null });
  const openReport = (v) => setReportModal({ open: true, id: v.visitNo, visit: v });
  const closeReportModal = () => setReportModal({ open: false, id: null, visit: null });

  const handleQuickAction = (a) => {
    if (a.nav) {
      onNavigate?.(a.nav);
    } else {
      showToast(`${a.title} — coming soon in v2.2.`, 'info');
    }
  };

  return (
    <Box>

      {/* ── 1. Greeting block ─────────────────────────────────────── */}
      <Box sx={{ mb: 4 }}>
        {/* <Kicker>Partner Dashboard</Kicker> */}
        <Typography
          component="h1"
          sx={{
            fontFamily: DISPLAY,
            fontSize: { xs: 32, md: 44 },
            fontWeight: 700,
            letterSpacing: '0.01em',
            textTransform: 'uppercase',
            color: GREEN_DEEP,
            mt: 1.5,
            mb: 1,
            lineHeight: 1,
          }}
        >
          Welcome,{' '}
          <Box component="span" sx={{ color: TEAL_DEEP, fontStyle: 'normal' }}>
            {displayName}.
          </Box>
        </Typography>
        <Typography
          sx={{
            fontSize: 14.5,
            color: MP.textMuted,
            maxWidth: '70ch',
          }}
        >
          {isDoctor ? (
            <>
              Doctor at{' '}
              <Box component="b" sx={{ color: MP.text, fontWeight: 700 }}>
                {orgName}
              </Box>
              {doctor?.specialization ? <> · {doctor.specialization}</> : null}
            </>
          ) : (
            <>
              {orgName} ·{' '}
              <Box component="b" sx={{ color: MP.text, fontWeight: 700 }}>
                {PARTNER_TIER}
              </Box>
              {' '}· Territory: {PARTNER_TERRITORY}
            </>
          )}
        </Typography>
      </Box>

      {/* ── 2. 4-stat strip ────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 0,
          border: `1px solid ${MP.border}`,
          bgcolor: MP.white,
          mb: 4,
          '& > div': {
            borderRight: { md: `1px solid ${MP.border}` },
            borderBottom: { xs: `1px solid ${MP.border}`, md: 'none' },
          },
          '& > div:nth-of-type(2)': {
            borderRight: { md: `1px solid ${MP.border}` },
          },
          '& > div:last-of-type': {
            borderRight: 'none',
            borderBottom: 'none',
          },
          '& > div:nth-last-of-type(-n+2)': {
            borderBottom: { xs: 'none', md: 'none' },
          },
        }}
      >
        <Stat
          value={String(monthStats.thisCount)}
          label={`Requisitions · ${monthStats.thisLabel}`}
          hint={monthStats.momHint}
        />
        <Stat
          value={String(monthStats.inProgress)}
          label="In Progress Now"
          hint="Live tracking below"
          accent={TEAL_DEEP}
        />
        <Stat
          value={monthStats.testsThisMonth.toLocaleString('en-IN')}
          label="Tests Processed · MTD"
          hint={
            monthStats.thisCount > 0
              ? `Avg ${monthStats.avgTestsPerVisit.toFixed(1)} per visit`
              : 'No requisitions yet this month'
          }
          accent="#B8860B"
        />
        <Stat
          value={totalTestsAvailable != null ? totalTestsAvailable.toLocaleString('en-IN') : '—'}
          label="Tests Available"
          hint="All branches · NABL in-scope"
        />
      </Box>

      {/* ── 2b. Operational KPI strip (carried over from medical portal) ──
          Same four cards the lab portal had before — tracks real-time
          report production, NOT partner economics. Sits below the
          partner-economics strip so both views are visible at a glance. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 4,
        }}
      >
        <StatCard
          tone="teal"
          label="Pending Reports"
          value={String(monthStats.pending)}
          sub={monthStats.pending === 0 ? 'All caught up' : 'Awaiting lab sign-off'}
        />
        <StatCard tone="orange" label="Open Tickets" value="2" sub="1 awaiting response" />
        <StatCard
          tone="red"
          label="Open Queries"
          value={String(rcaStats.open)}
          sub={rcaStats.open === 0 ? 'None pending' : `${rcaStats.open} awaiting response`}
        />
        <StatCard
          tone="green"
          label="Resolved This Month"
          value={String(rcaStats.resolvedThisMonth)}
          sub={rcaStats.resolvedTotal > 0 ? `${rcaStats.resolvedTotal} resolved overall` : 'No closures yet'}
        />
      </Box>

      {/* Pricing summary banner removed per product call. The L2L rate
          is a future v2 conversation — show numbers only once we have
          actual partner-tier data wired in. */}

      {/* ── 4. Quick Actions — centre/partner only ─────────────────────
          Doctor sessions don't need the partner-tier tiles (TRF
          downloads, marketing kit, API access, etc.) and have
          Reports & Analytics in the sidebar instead, so we hide
          the whole section in doctor mode. */}
      {!isDoctor && (
        <>
          <SectionHead title="Quick Actions" right="For your role & tier" />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 1.5,
            }}
          >
            {QUICK_ACTIONS.map((a) => {
              const Icon = a.icon;
              return (
                <Box
                  key={a.id}
                  onClick={() => handleQuickAction(a)}
                  sx={{
                    bgcolor: MP.white,
                    border: `1.5px solid ${MP.border}`,
                    p: 2.25,
                    cursor: 'pointer',
                    transition: 'all .15s',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 130,
                    '&:hover': {
                      borderColor: MP.teal,
                      transform: 'translateY(-1px)',
                      boxShadow: '0 1px 0 rgba(21,32,26,.04), 0 2px 4px rgba(21,32,26,.04)',
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 22, color: MP.tealDark, mb: 1 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: MP.text, mb: 0.5 }}>
                    {a.title}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: MP.textMuted, lineHeight: 1.4, flex: 1 }}>
                    {a.sub}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: MONO,
                      fontSize: 14,
                      color: MP.tealDark,
                      mt: 1,
                      fontWeight: 700,
                    }}
                  >
                    →
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </>
      )}

      {/* ── 5. Recent Requisitions ────────────────────────────────── */}
      <SectionHead
        title="Recent Requisitions"
        right={
          <Box
            component="span"
            onClick={() => onNavigate?.('reports')}
            sx={{
              cursor: 'pointer',
              color: MP.tealDark,
              fontWeight: 700,
              fontSize: 12,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            View all →
          </Box>
        }
      />
      <Box
        sx={{
          border: `1px solid ${MP.border}`,
          bgcolor: MP.white,
          overflow: 'auto',
        }}
      >
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13,
            minWidth: 760,
            '& th, & td': {
              textAlign: 'left',
              padding: '12px 14px',
              borderBottom: `1px solid ${MP.border}`,
              verticalAlign: 'middle',
            },
            '& th': {
              bgcolor: MP.bg,
              fontFamily: MONO,
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              color: MP.textMuted,
              fontWeight: 700,
              whiteSpace: 'nowrap',
            },
            '& tr:hover td': { bgcolor: MP.tealLight },
            '& tr:last-of-type td': { borderBottom: 'none' },
          }}
        >
          <thead>
            <tr>
              <th style={{ width: 160 }}>Visit · UHID</th>
              <th>Patient · Tests</th>
              <th style={{ width: 120 }}>Status</th>
              <th style={{ width: 130 }}>Date</th>
              <th style={{ width: 110, textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {salesLoading && !recentVisits.length && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: 28, color: MP.textMuted }}>
                  Loading recent visits…
                </td>
              </tr>
            )}
            {!salesLoading && recentVisits.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: 28, color: MP.textMuted }}>
                  No requisitions yet — patients you refer will show up here.
                </td>
              </tr>
            )}
            {recentVisits.map((v) => {
              // Three names, not one. "CBC + 6 more" tells you almost
              // nothing about what the visit was for, and the column has
              // the room for three.
              const names = v.tests.map((t) => t.name).filter(Boolean);
              const shownTests = names.slice(0, 3);
              const moreCount = Math.max(0, names.length - shownTests.length);
              return (
                <tr key={`${v.visitNo}-${v.billNo}`} style={{ cursor: 'pointer' }} onClick={() => openReport(v)}>
                  <td>
                    <Box sx={{ fontFamily: MONO, fontSize: 11, color: MP.text, fontWeight: 700 }}>
                      {v.visitNo}
                    </Box>
                    <Box sx={{ fontFamily: MONO, fontSize: 10.5, color: MP.tealDark, fontWeight: 700, mt: 0.25 }}>
                      {v.uhid}
                    </Box>
                  </td>
                  <td>
                    <Box sx={{ fontWeight: 700, color: MP.text }}>{v.patient || 'Unknown'}</Box>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: '4px' }}>
                      {shownTests.map((t) => (
                        <Box
                          key={t}
                          sx={{
                            bgcolor: MP.tealLight, color: MP.teal,
                            fontSize: 11, lineHeight: 1.6,
                            px: '8px', py: '2px', borderRadius: '20px',
                            maxWidth: 200, overflow: 'hidden',
                            whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                          }}
                        >
                          {t}
                        </Box>
                      ))}
                      {moreCount > 0 && (
                        <Box sx={{
                          bgcolor: MP.greyLight, color: MP.grey,
                          fontSize: 11, lineHeight: 1.6,
                          px: '8px', py: '2px', borderRadius: '20px',
                        }}>
                          +{moreCount} more
                        </Box>
                      )}
                      {names.length === 0 && (
                        <Box sx={{ fontSize: 11.5, color: MP.ink3 }}>No tests listed</Box>
                      )}
                    </Stack>
                  </td>
                  <td><StatusPill status={v.status} /></td>
                  <td>
                    <Box component="span" sx={{ fontFamily: MONO, fontSize: 11, color: MP.textMuted }}>
                      {prettyDate(v.billDate)}
                    </Box>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Box component="span" sx={{ fontFamily: MONO, color: MP.text, fontWeight: 700 }}>
                      ₹{(v.totalAmount || 0).toLocaleString('en-IN')}
                    </Box>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Box>
      </Box>

      {/* Click-to-open report modal — same component the medical &
          lab Patient Reports pages use. */}
      <ReportDetailModal
        open={reportModal.open}
        onClose={closeReportModal}
        reportId={reportModal.id}
        visit={reportModal.visit}
        // Without this the modal hides its Raise Query button entirely -
        // it renders the CTA only when a handler is supplied. Opening a
        // requisition from the dashboard therefore dead-ended, while the
        // same modal opened from Patient Reports could raise a query.
        //
        // 'new-complaint' is an alias the dashboards map to 'new-query',
        // and the visit rides along so RaiseQuery lands pre-filled rather
        // than making the user pick the patient again.
        onRaiseQuery={(visit) => {
          closeReportModal();
          onNavigate?.('new-complaint', { visit });
        }}
      />

      {/* ── 6. TAT Performance ─────────────────────────────────────
          Single card scoped to the signed-in centre (or doctor),
          showing Avg TAT + on-time delivery for the last 7 days
          ending yesterday. Mirrors the Analytics page's window so the
          two surfaces always agree on "last week". Shimmer renders
          on first load AND every refetch so stale numbers never sit
          under a refreshing card. */}
      <SectionHead
        title="TAT Performance · Last 7 Days"
        right={
          <Box
            component="span"
            onClick={() => onNavigate?.('analytics')}
            sx={{
              cursor: 'pointer',
              color: MP.tealDark,
              fontWeight: 700,
              fontSize: 12,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            View full report →
          </Box>
        }
      />
      <Box
        sx={{
          border: `1px solid ${MP.border}`,
          bgcolor: MP.white,
          p: { xs: 2, md: 2.5 },
        }}
      >
        {tatLoading || tatFetching ? (
          // Skeleton shaped like the real card so the layout doesn't
          // jump when the data resolves.
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} divider={
            <Box sx={{ display: { xs: 'none', md: 'block' }, width: '1px', bgcolor: MP.border }} />
          }>
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={{ flex: 1 }}>
                <Skeleton variant="text" width={80} height={12} sx={{ mb: 1 }} />
                <Skeleton variant="text" width={120} height={36} />
                <Skeleton variant="text" width={140} height={12} sx={{ mt: 0.5 }} />
              </Box>
            ))}
          </Stack>
        ) : (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            divider={
              <Box sx={{ display: { xs: 'none', md: 'block' }, width: '1px', bgcolor: MP.border }} />
            }
          >
            {/* Avg TAT */}
            <Box sx={{ flex: 1 }}>
              <Typography sx={{
                fontFamily: MONO, fontSize: 10, color: MP.textMuted,
                textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, mb: 0.75,
              }}>
                Avg TAT
              </Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: { xs: 24, md: 27 }, fontWeight: 600, letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums', color: MP.text, lineHeight: 1 }}>
                {formatMinutesLO(tatStats.avg)}
              </Typography>
              <Typography sx={{ fontSize: 11, color: MP.textMuted, mt: 0.5 }}>
                Booking → Approval (avg)
              </Typography>
            </Box>

            {/* On-time % */}
            <Box sx={{ flex: 1 }}>
              <Typography sx={{
                fontFamily: MONO, fontSize: 10, color: MP.textMuted,
                textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, mb: 0.75,
              }}>
                On-Time Delivery
              </Typography>
              <Typography sx={{
                fontFamily: DISPLAY,
                fontSize: { xs: 28, md: 32 },
                color: tatStats.onTimePct == null
                  ? MP.textMuted
                  : tatStats.onTimePct >= 90 ? MP.green
                  : tatStats.onTimePct >= 75 ? MP.teal
                  : tatStats.onTimePct >= 50 ? MP.orange
                  : MP.red,
                lineHeight: 1,
              }}>
                {tatStats.onTimePct == null ? '—' : `${tatStats.onTimePct}%`}
              </Typography>
              <Typography sx={{ fontSize: 11, color: MP.textMuted, mt: 0.5 }}>
                {tatStats.inTat} in TAT · {tatStats.outTat} out
              </Typography>
            </Box>

            {/* Total reports — context for the two ratios above. */}
            <Box sx={{ flex: 1 }}>
              <Typography sx={{
                fontFamily: MONO, fontSize: 10, color: MP.textMuted,
                textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, mb: 0.75,
              }}>
                Total Reports
              </Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: { xs: 24, md: 27 }, fontWeight: 600, letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums', color: MP.text, lineHeight: 1 }}>
                {tatStats.total}
              </Typography>
              <Typography sx={{ fontSize: 11, color: MP.textMuted, mt: 0.5 }}>
                {tatRange.from} → {tatRange.to}
              </Typography>
            </Box>
          </Stack>
        )}
      </Box>

    </Box>
  );
};

export default LabOverview;
